import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Check, Plus, Search, Upload, X, FileText, MessageSquare, File } from 'lucide-react';
import {
  projectsApi,
  resourcesApi,
  documentApi,
  Document,
  DocumentListItem,
  ChatSessionListItem,
  ProjectFileItem,
  subjectRoots,
} from '../../../services/api';
import { useTopics } from '../../../hooks/useTopics';
import { useChatSessions } from '../../../hooks/useChatSessions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

type FilterKind = 'all' | 'mine' | 'shared';
type SortOption = 'title-asc' | 'title-desc' | 'updated-desc' | 'updated-asc';

const FETCH_BY_API_VALUE: Record<string, (limit: number) => Promise<DocumentListItem[]>> = {
  government_regulatory: (l) => resourcesApi.getGovernmentRegulatory(l),
  coding_classification:  (l) => resourcesApi.getCodingClassification(l),
  hospital_advocacy:      (l) => resourcesApi.getHospitalAdvocacy(l),
  payer_policies:         (l) => resourcesApi.getPayerPolicies(l),
  denials_appeals:        (l) => resourcesApi.getDenialsAppeals(l),
  technology_benchmarks:  (l) => resourcesApi.getTechnologyBenchmarks(l),
};

function filterAndSort(docs: DocumentListItem[], q: string, sort: SortOption): DocumentListItem[] {
  const query = q.trim().toLowerCase();
  const filtered = query
    ? docs.filter((d) => d.title.toLowerCase().includes(query))
    : [...docs];
  return filtered.sort((a, b) => {
    switch (sort) {
      case 'title-asc': return (a.title ?? '').localeCompare(b.title ?? '');
      case 'title-desc': return (b.title ?? '').localeCompare(a.title ?? '');
      case 'updated-desc': {
        const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return tb - ta;
      }
      case 'updated-asc': {
        const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return ta - tb;
      }
      default: return 0;
    }
  });
}

// --- Documents tab ---
function DocumentsTab({
  projectId,
  existingDocumentIds,
  onDocumentAdded,
}: {
  projectId: string;
  existingDocumentIds: Set<string>;
  onDocumentAdded: (docId: string) => void;
}) {
  const { topics } = useTopics();
  const rcmTopics = useMemo(
    () => subjectRoots(topics).filter((t) => t.code),
    [topics]
  );
  const firstApiValue = rcmTopics[0]?.code ?? '';
  const [selectedApiValue, setSelectedApiValue] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('updated-desc');
  const [docs, setDocs] = useState<DocumentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set(existingDocumentIds));

  useEffect(() => {
    setAdded(new Set(existingDocumentIds));
  }, [existingDocumentIds]);

  // Once topics load, initialise the selected value to the first available tag root
  useEffect(() => {
    if (!selectedApiValue && firstApiValue) {
      setSelectedApiValue(firstApiValue);
    }
  }, [firstApiValue, selectedApiValue]);

  const loadDocs = useCallback(async (apiValue: string, query?: string) => {
    setLoading(true);
    try {
      if (query?.trim()) {
        const topic = rcmTopics.find((t) => t.code === apiValue);
        const response = await documentApi.searchDocuments({
          query: query.trim(),
          tagId: topic?.id,
          limit: 50,
        });
        setDocs(response.results ?? []);
        return;
      }
      const fetchFn = FETCH_BY_API_VALUE[apiValue];
      if (!fetchFn) {
        setDocs([]);
        return;
      }
      const data = await fetchFn(50);
      setDocs(data);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [rcmTopics]);

  useEffect(() => {
    if (!selectedApiValue) return;
    const timer = window.setTimeout(() => {
      void loadDocs(selectedApiValue, searchQuery);
    }, searchQuery.trim() ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [selectedApiValue, searchQuery, loadDocs]);

  const displayed = filterAndSort(docs, searchQuery.trim() ? '' : searchQuery, sortOption);

  const handleAdd = async (doc: DocumentListItem) => {
    if (added.has(doc.id) || adding.has(doc.id)) return;
    setAdding((prev) => new Set(prev).add(doc.id));
    try {
      await projectsApi.addDocument(projectId, doc.id);
      setAdded((prev) => new Set(prev).add(doc.id));
      onDocumentAdded(doc.id);
    } finally {
      setAdding((prev) => { const next = new Set(prev); next.delete(doc.id); return next; });
    }
  };

  return (
    <div className="flex flex-col gap-3 max-w-full overflow-hidden">
      {/* Filters row: tag root dropdown + sort dropdown */}
      <div className="flex gap-2">
        <Select
          value={selectedApiValue}
          onValueChange={setSelectedApiValue}
        >
          <SelectTrigger className="h-9 flex-1 min-w-0">
            <SelectValue placeholder="Tag" />
          </SelectTrigger>
          <SelectContent>
            {rcmTopics.map((t) => (
              <SelectItem key={t.code} value={t.code!}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sortOption}
          onValueChange={(v) => setSortOption(v as SortOption)}
        >
          <SelectTrigger className="h-9 w-44 flex-shrink-0">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated-desc">Newest first</SelectItem>
            <SelectItem value="updated-asc">Oldest first</SelectItem>
            <SelectItem value="title-asc">Title A–Z</SelectItem>
            <SelectItem value="title-desc">Title Z–A</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search documents…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* List */}
      <div className="h-72 overflow-y-scroll" style={{ maxWidth: '620px' }}>
        <div className="pr-1 w-full min-w-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              {searchQuery ? 'No documents match your search.' : 'No documents for this tag.'}
            </div>
          ) : (
            <div className="space-y-1">
              {displayed.map((doc) => {
                const isAdded = added.has(doc.id);
                const isAdding = adding.has(doc.id);
                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-accent/50 transition-colors w-full min-w-0"
                  >
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {doc.tags?.[0]?.label && (
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {doc.tags[0].label}
                          </span>
                        )}
                        {doc.updated_at && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(doc.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant={isAdded ? 'secondary' : 'outline'}
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => handleAdd(doc)}
                      disabled={isAdded || isAdding}
                      aria-label={isAdded ? 'Already added' : `Add ${doc.title}`}
                    >
                      {isAdding ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : isAdded ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Chats tab ---
function ChatsTab({
  projectId,
  existingChatIds,
  onChatAdded,
}: {
  projectId: string;
  existingChatIds: Set<string>;
  onChatAdded: (chatId: string) => void;
}) {
  const { sessions, loading } = useChatSessions();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKind>('all');
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set(existingChatIds));

  useEffect(() => {
    setAdded(new Set(existingChatIds));
  }, [existingChatIds]);

  const filtered = sessions.filter((s) => {
    const matchSearch = !search.trim() || (s.title || '').toLowerCase().includes(search.trim().toLowerCase());
    if (!matchSearch) return false;
    if (filter === 'mine') return s.is_owner === true;
    if (filter === 'shared') return s.is_owner === false;
    return true;
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const handleAdd = async (session: ChatSessionListItem) => {
    if (added.has(session.id) || adding.has(session.id)) return;
    setAdding((prev) => new Set(prev).add(session.id));
    try {
      await projectsApi.addChat(projectId, session.id);
      setAdded((prev) => new Set(prev).add(session.id));
      onChatAdded(session.id);
    } finally {
      setAdding((prev) => { const next = new Set(prev); next.delete(session.id); return next; });
    }
  };

  return (
    <div className="flex flex-col gap-3 max-w-full overflow-hidden">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search chats…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* Filter pills */}
      <div className="flex gap-1">
        {(['all', 'mine', 'shared'] as FilterKind[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors',
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            {f === 'all' ? 'All' : f === 'mine' ? 'Created by me' : 'Shared with me'}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="h-72 overflow-y-auto overflow-x-hidden">
        <div className="pr-1 w-full min-w-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              {search.trim() ? 'No chats match your search.' : 'No chats found.'}
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((session) => {
                const isAdded = added.has(session.id);
                const isAdding = adding.has(session.id);
                const title = session.title?.trim() || 'New chat';
                return (
                  <div
                    key={session.id}
                    className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-accent/50 transition-colors w-full min-w-0"
                  >
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{title}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {!session.is_owner && session.role && (
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded capitalize">
                            {session.role}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(session.updated_at ?? session.created_at)}
                        </span>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant={isAdded ? 'secondary' : 'outline'}
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => handleAdd(session)}
                      disabled={isAdded || isAdding}
                      aria-label={isAdded ? 'Already added' : `Add chat "${title}"`}
                    >
                      {isAdding ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : isAdded ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- PDFs tab ---
function PDFsTab({
  projectId,
  onFileAdded,
}: {
  projectId: string;
  onFileAdded: () => void;
}) {
  const [files, setFiles] = useState<ProjectFileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectsApi.getFiles(projectId);
      setFiles(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Only PDF files are supported.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('File too large (max 20 MB).');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const uploaded = await projectsApi.uploadFile(projectId, file);
      setFiles((prev) => [uploaded, ...prev]);
      onFileAdded();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : 'Upload failed.';
      setUploadError(typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemove = async (fileId: string) => {
    setRemoving((prev) => new Set(prev).add(fileId));
    try {
      await projectsApi.removeFile(projectId, fileId);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      onFileAdded();
    } finally {
      setRemoving((prev) => { const next = new Set(prev); next.delete(fileId); return next; });
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col gap-4 max-w-full overflow-hidden">
      {/* Upload area */}
      <div
        className={cn(
          'border-2 border-dashed rounded-xl p-6 text-center transition-colors flex-shrink-0 cursor-pointer',
          dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
        )}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        aria-label="Upload PDF file"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileInput}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Spinner className="h-8 w-8 text-primary" />
            <p className="text-sm text-muted-foreground">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              Drop a PDF here or click to browse
            </p>
            <p className="text-xs text-muted-foreground">PDF files up to 20 MB</p>
          </div>
        )}
      </div>

      {uploadError && (
        <p className="text-sm text-destructive -mt-2 flex-shrink-0">{uploadError}</p>
      )}

      {/* Existing files */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Uploaded files
        </h4>
        <div className="h-44 overflow-y-auto overflow-x-hidden">
          <div className="pr-1 w-full min-w-0">
            {loading ? (
              <div className="flex justify-center py-6">
                <Spinner className="h-6 w-6" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                No PDFs uploaded yet.
              </div>
            ) : (
              <div className="space-y-1">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-accent/50 transition-colors group w-full min-w-0"
                  >
                    <div className="h-8 w-8 rounded-md bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <File className="h-4 w-4 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{file.file_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatSize(file.file_size)} · {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleRemove(file.id)}
                      disabled={removing.has(file.id)}
                      aria-label={`Remove ${file.file_name}`}
                    >
                      {removing.has(file.id) ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : (
                        <X className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main dialog ---
interface AddToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  existingDocumentIds: Set<string>;
  existingChatIds: Set<string>;
  onItemAdded: () => void;
}

export function AddToProjectDialog({
  open,
  onOpenChange,
  projectId,
  existingDocumentIds,
  existingChatIds,
  onItemAdded,
}: AddToProjectDialogProps) {
  const [activeTab, setActiveTab] = useState('documents');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-semibold">Add to library</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-6 pt-4">
            <TabsList className="w-full justify-start bg-muted/60">
              <TabsTrigger value="documents" className="gap-2">
                <FileText className="h-4 w-4" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="chats" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Chats
              </TabsTrigger>
              <TabsTrigger value="pdfs" className="gap-2">
                <File className="h-4 w-4" />
                PDFs
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="documents" className="mt-0 px-6 pb-6 pt-4">
            <DocumentsTab
              projectId={projectId}
              existingDocumentIds={existingDocumentIds}
              onDocumentAdded={() => onItemAdded()}
            />
          </TabsContent>

          <TabsContent value="chats" className="mt-0 px-6 pb-6 pt-4">
            <ChatsTab
              projectId={projectId}
              existingChatIds={existingChatIds}
              onChatAdded={() => onItemAdded()}
            />
          </TabsContent>

          <TabsContent value="pdfs" className="mt-0 px-6 pb-6 pt-4">
            <PDFsTab
              projectId={projectId}
              onFileAdded={() => onItemAdded()}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
